<template>
  <v-row justify="center">
    <v-dialog
      v-model="open"
      width="600px"
      :fullscreen="isMobileBreakpoint"
      :persistent="isMobileBreakpoint || currentReactivationType === REACTIVATION_TYPE.AFTER_CYCLE"
    >
      <v-card>
        <v-toolbar
          elevation="0"
          color="background"
          class="pt-4 pb-4"
          style="height: 72px;"
        >
          <v-toolbar-title class="toolbar-title ml-2">
            {{ modalContent.title }}
            <p v-if="modalContent.subtitle" class="text-caption">
              {{ modalContent.subtitle }}
            </p>
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pl-6 pr-6 pt-0 pb-6">
          <p
            v-if="modalContent.descriptionBold"
            class="body-bold-medium mb-2"
          >
            {{ modalContent.descriptionBold }}
          </p>
          <p
            class="body-regular-small"
            :class="currentReactivationType === REACTIVATION_TYPE.AFTER_CYCLE ? 'mb-0' : 'mb-4'"
          >
            {{ modalContent.description }}
          </p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions
          class="pa-7"
          :class="isMobileBreakpoint ? 'mobile-actions' : ''"
        >
          <div
            class="d-flex justify-space-between"
            style="width: 100%;"
          >
            <div class="d-flex align-center">
              <a
                type="secondary"
                size="xsmall"
                class="brand-60-base--text"
                style="font-size: 14px;"
                @click="openCrispChat"
              >
                {{ $t('message.pages.organization.reactivateAccount.afterCycle.contact') }}
                <v-icon
                  size="14px"
                  class="page__progressBar--article-icon brand-60-base--text"
                >
                  mdi-open-in-new
                </v-icon>
              </a>
            </div>
            <div>
              <base-btn
                large
                :loading="isFetching"
                :disabled="modalContent.primaryBtn.disabled"
                @click="modalContent.primaryBtn.action"
              >
                {{ modalContent.primaryBtn.label }}
              </base-btn>
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import planApi from '@/services/planApi'

const REACTIVATION_TYPE = {
  AFTER_CYCLE: 'AFTER_CYCLE',
}

export default {
  name: 'AccountReactivationModal',
  data: () => ({
    REACTIVATION_TYPE,
    open: false,
    isFetching: false,
    currentReactivationType: REACTIVATION_TYPE.AFTER_CYCLE,
    isActivatingAccount: false,
  }),
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    handleAccountReactivationModal() {
      this.resetState()
      this.hideCrispButton()
      this.currentReactivationType = REACTIVATION_TYPE.AFTER_CYCLE
      this.open = true
    },
    async handleReactivation() {
      this.isFetching = true

      try {
        await planApi.reactivateSubscription()
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          type: 'success',
          text: this.$t('message.pages.organization.reactivateAccount.afterCycle.reactivateSuccess'),
        })
        this.handleClose()
      } catch (error) {
        console.error(error)
      } finally {
        this.isFetching = false
      }
    },
    handleChoosePlan() {
      this.handleClose()
      this.$router.push({ name: 'Cart' })
    },
    handleClose() {
      this.open = false
      this.showCrispButton()
      this.$root.$emit('accountReactivationModalHasClosed')
    },
    resetState() {
      this.isFetching = false
    },
    openCrispChat() {
      if (typeof this.$crisp !== 'undefined') {
        this.$crisp.push(['do', 'chat:show'])
        this.$crisp.push(['do', 'chat:open'])
      }
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton && this.isMobileBreakpoint) {
        crispButton.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton) {
        crispButton.style.removeProperty('display')
      }
    },
    checkAccountReactivation() {
      const currentRoute = this.$route.name
      const planInfo = this.userPlanInfo

      const subscriptionReady = planInfo?.hasOwnProperty('subscription')
      const noPlan = subscriptionReady && planInfo.subscription === null

      const isAllowedRoute = !['Cart', 'Login', 'SignUp', 'FirstLoading', 'Organization', 'UserPlan'].includes(currentRoute)

      if (!subscriptionReady) {
        return
      }

      if (this.isActivatingAccount && noPlan) {
        this.isActivatingAccount = false
        return
      }

      if (noPlan && isAllowedRoute) {
        this.handleAccountReactivationModal()
      }
    }
  },
  computed: {
    ...mapState({
      userPlanInfo: (state) => state.user.userPlanInfo,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    modalContent() {
      const map = {
        [REACTIVATION_TYPE.AFTER_CYCLE]: {
          title: this.$t('message.pages.organization.reactivateAccount.afterCycle.title'),
          subtitle: null,
          descriptionBold: this.$t('message.pages.organization.reactivateAccount.afterCycle.descriptionBold'),
          description: this.$t('message.pages.organization.reactivateAccount.afterCycle.description'),
          checkbox: false,
          checkboxLabel: null,
          primaryBtn: {
            label: this.$t('message.pages.organization.reactivateAccount.afterCycle.choosePlan'),
            action: this.handleChoosePlan,
            disabled: false,
          },
        },
      }

      return map[this.currentReactivationType] || {}
    }
  },
  watch: {
    '$route.name': {
      handler() {
        this.checkAccountReactivation()
      },
      immediate: true,
    },
    userPlanInfo: {
      handler() {
        this.checkAccountReactivation()
      },
      deep: true,
    },
  },
  mounted() {
    this.checkAccountReactivation()
    this.$root.$on('accountReactivationModal', this.handleAccountReactivationModal)
    this.$root.$on('openCheckoutDoneModal', () => {
      this.isActivatingAccount = true
    })
  },
  beforeDestroy() {
    this.showCrispButton()
    this.$root.$off('accountReactivationModal')
    this.$root.$off('openCheckoutDoneModal')
    this.$root.$emit('accountReactivationModalHasClosed')
  },
}
</script>

<style lang="scss" scoped>
.toolbar-title {
  color: varFigma(neutral-20-dark);
  font-size: 18px;
  font-weight: 500;
  line-height: 120%;
}

.text-caption {
  color: varFigma(neutral-40-light);
  font-size: 12px;
  line-height: 150%;
  margin-top: 4px;
  margin-bottom: 0;
}

.mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: var(--v-background-base);
  z-index: 10;
  padding: 16px;
}

.v-toolbar__title {
  white-space: normal;
}

::v-deep .v-toolbar__content {
  align-items: baseline;
  height: auto !important;

  .v-toolbar__items {
    height: unset;

    button {
      border-radius: 50%;
      width: 31.2px !important;
      height: 31.2px !important;
    }
  }
}

@media (max-width: 360px) {
  .mobile-actions {
    padding: 16px !important;

    div {
      justify-content: center !important;
    }

    div > div {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
